import { Group, Stack } from "@mantine/core"
import { useDisclosure } from "@mantine/hooks"
import { IconPlus } from "@tabler/icons-react"
import { Button, SearchInput } from "@tfg/ui"
import { t } from "i18next"
import { useState } from "react"

import { AdminResponses } from "@tfg/types/endpoints/admin"
import {
  standardsCreateSchema,
  standardsCreateValidationType,
} from "@tfg/types/schemas/admin"

import { HeaderControlsProvider } from "~/components/contexts/useHeaderControlsContext"
import {
  FindStandardsWithCertificateTypesQueryResponse,
  FindStandardsWithCertificateTypesRequestSortsDto,
  useFindStandardsWithCertificateTypes,
} from "~/gen/api"
import { usePaginatedQuery } from "~/hooks/usePaginatedQuery"
import { StandardSorts } from "~/pages/Admin/MasterLists/CertificatesTypes/Standards/StandardSorts"
import { StandardsTable } from "~/pages/Admin/MasterLists/CertificatesTypes/Standards/StandardsTable"
import { FormFieldConfig } from "~/pages/Admin/components/TypesForm"

import { DrawerForm } from "../DrawerForm"

import { useCreateStandardMutation } from "./hooks"

interface Props {
  certificatesTypesList: AdminResponses.CertificateTypeList[]
}

const StandardsContent = ({ certificatesTypesList }: Props) => {
  const [search, setSearch] = useState("")
  const [sorts, setSorts] =
    useState<FindStandardsWithCertificateTypesRequestSortsDto>({
      id: {
        value: "asc",
        rank: 0,
      },
    })
  const { data, pageBasedPagination, totalCount, setPageBasedPagination } =
    usePaginatedQuery<
      typeof useFindStandardsWithCertificateTypes,
      FindStandardsWithCertificateTypesQueryResponse
    >(useFindStandardsWithCertificateTypes, {
      search,
      sorts,
    })

  const [drawerOpen, drawerActions] = useDisclosure(false)
  const { mutate: createStandard } = useCreateStandardMutation({
    onSuccess: drawerActions.close,
  })

  const formattedOptions = certificatesTypesList.map((option) => ({
    value: option.id.toString(),
    label: option.key,
  }))
  const fields: FormFieldConfig[] = [
    {
      type: "text",
      name: "name",
      label: "Name",
      placeholder: "Name",
    },
    {
      type: "multiselect",
      name: "certificateTypes",
      label: "Certificates",
      placeholder: "Certificates",
      options: formattedOptions,
    },
  ]

  return (
    <Stack>
      <Group justify="space-between" mt="sm">
        <Group>
          <SearchInput
            size="xs"
            w={200}
            inputValue={search}
            onChange={(value) => setSearch(value.toString())}
            placeholder={t("admin:standards.search.label")}
          />
          <StandardSorts sorts={sorts} setSorts={setSorts} />
        </Group>
        <Button onClick={() => drawerActions.open()} icon={<IconPlus />}>
          New Standard
        </Button>
      </Group>
      {drawerOpen ? (
        <DrawerForm<standardsCreateValidationType>
          drawerActions={drawerActions}
          schema={standardsCreateSchema}
          initialValues={{
            name: "",
            certificateTypes: [],
          }}
          mutate={createStandard}
          fields={fields}
        />
      ) : null}
      {data?.data && (
        <StandardsTable
          standards={data.data}
          total={totalCount}
          certificatesTypesList={certificatesTypesList}
          pagination={pageBasedPagination}
          setPagination={setPageBasedPagination}
        />
      )}
    </Stack>
  )
}

export const Standards = ({ certificatesTypesList }: Props) => {
  return (
    <HeaderControlsProvider>
      <StandardsContent certificatesTypesList={certificatesTypesList} />
    </HeaderControlsProvider>
  )
}
